import React from "react";
import { X, MapPin, Clock, Calendar, User, Building2, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent } from "../../../shadcn/ui/dialog";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import AddonsBreakdown from "../../../components/addons/AddonsBreakdown";
import { getEventName } from "../../../utils/spaceNamingUtils";

const BookingDetailsModal = ({ isOpen, onClose, booking }) => {
  if (!booking) return null;

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const getDuration = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const diffMs = e - s;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs / (1000 * 60)) % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getStatusColor = status => {
    const colors = {
      CONFIRMED: "bg-green-100 text-green-700",
      PENDING: "bg-yellow-100 text-yellow-700",
      CANCELLED: "bg-red-100 text-red-700",
      COMPLETED: "bg-blue-100 text-blue-700",
      NO_SHOW: "bg-gray-100 text-gray-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const images = booking.media?.images || [];
  const spaceInstance = booking.spaceInstance;
  const location = spaceInstance?.location;
  const buildingAddress = location?.buildingAddress;
  const bookedBy = booking.bookedBy;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0">
        {/* Header with Image */}
        <div className="relative">
          {images.length > 0 ? (
            <div className="relative h-64 w-full">
              <img src={images[0]} className="object-cover w-full h-full" alt="Space" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {images.length > 1 && (
                <div className="absolute bottom-4 right-4 px-3 py-1.5 text-sm font-medium text-white bg-black/60 rounded-lg backdrop-blur-sm">
                  +{images.length - 1} more
                </div>
              )}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-white bg-black/40 rounded-full backdrop-blur-sm hover:bg-black/60 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="relative flex justify-center items-center h-64 w-full bg-gradient-to-br from-blue-50 to-indigo-50">
              <Building2 className="w-20 h-20 text-blue-300" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-600 bg-white rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-16rem)]">
          {/* Title and Status */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{spaceInstance?.title || getEventName(spaceInstance) || "Space Booking"}</h2>
              {buildingAddress && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">
                    {[
                      buildingAddress.buildingName,
                      buildingAddress.streetAddress,
                      buildingAddress.city,
                      buildingAddress.region,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>
            <Badge className={getStatusColor(booking.status)}>{booking.status?.replace("_", " ")}</Badge>
          </div>

          {/* Booking Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-blue-900 uppercase tracking-wide">Date</div>
                  <div className="text-sm font-semibold text-gray-900">{formatDate(booking.startTime)}</div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-purple-900 uppercase tracking-wide">Time</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">{getDuration(booking.startTime, booking.endTime)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Booked By */}
          {bookedBy && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Booked By</div>
                  <div className="flex items-center gap-2">
                    {bookedBy.imageUrl && (
                      <img
                        src={bookedBy.imageUrl}
                        alt={bookedBy.firstName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {bookedBy.firstName} {bookedBy.lastName}
                      </div>
                      <div className="flex flex-col">
                        {bookedBy.username && (
                          <div className="text-xs text-gray-500 lowercase">@{bookedBy.username.replace("@", "")}</div>
                        )}
                        <div className="text-xs text-gray-400">{bookedBy.email}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Addons Breakdown */}
          {booking.addons && booking.addons.length > 0 && (
            <AddonsBreakdown addons={booking.addons} currency={booking.currency || "GHS"} />
          )}

          {/* Location Details */}
          {location && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Location Details</h3>
              <div className="space-y-2 text-sm">
                {location.name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium text-gray-900">{location.name}</span>
                  </div>
                )}
                {location.floorLevel && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Floor:</span>
                    <span className="font-medium text-gray-900">{location.floorLevel}</span>
                  </div>
                )}
                {buildingAddress?.buildingName && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Building:</span>
                    <span className="font-medium text-gray-900">{buildingAddress.buildingName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Images */}
          {images.length > 1 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Gallery</h3>
              <div className="grid grid-cols-3 gap-3">
                {images.slice(1, 7).map((img, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden group">
                    <img
                      src={img}
                      className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-300"
                      alt={`Space ${idx + 2}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking Reference */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-1">Booking ID</div>
                <div className="font-mono text-lg font-bold text-blue-700">#{booking.id}</div>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <Button onClick={onClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailsModal;
